import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import { DashboardLayout } from '@/layout/DashboardLayout';
import { useAuth } from '@/core/auth';
import { Button, Input } from '@/design-system';
import { StaffAccessLogTable } from './components/StaffAccessLogTable';
import { useStaffAccessLogsQuery } from './hooks/useStaffAccessLogsQuery';

export function StaffAccessLogsPage() {
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [staffEmailFilter, setStaffEmailFilter] = useState<string | undefined>(undefined);

  const { data, isLoading, refetch } = useStaffAccessLogsQuery(
    { page, limit: 50, staffEmail: staffEmailFilter },
    { enabled: user?.role === 'SUPERADMIN' }
  );

  const logs = data?.data ?? [];
  const meta = data?.meta ?? { total: 0, page: 1, limit: 50, totalPages: 1 };

  if (user && user.role !== 'SUPERADMIN') {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setStaffEmailFilter(search.trim() || undefined);
  };

  return (
    <DashboardLayout title="Logs de Acesso de Suporte">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-violet-600" />
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Logs de Acesso de Suporte</h1>
              <p className="text-sm text-gray-500">
                Registro de todas as sessões de impersonação realizadas pela equipe de suporte.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            disabled={isLoading}
            className="flex items-center gap-1.5"
          >
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            Atualizar
          </Button>
        </div>

        <form onSubmit={handleSearch} className="flex gap-2">
          <Input
            placeholder="Buscar por e-mail do staff…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs"
          />
          <Button type="submit" size="sm" variant="outline" className="flex items-center gap-1">
            <Search size={14} />
            Buscar
          </Button>
        </form>

        <StaffAccessLogTable logs={logs} isLoading={isLoading} />

        {meta.totalPages > 1 && (
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>
              {meta.total} registro{meta.total !== 1 ? 's' : ''} · página {meta.page} de {meta.totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="flex items-center gap-1"
              >
                <ChevronLeft size={14} />
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= meta.totalPages}
                onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
                className="flex items-center gap-1"
              >
                Próxima
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
